import React from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowLeft } from "lucide-react";

/**
 * The UK GDPR Arts.13-14 privacy notice — what the app tells a person BEFORE it collects
 * their data. This is a placeholder, not a reviewed legal document.
 *
 * Do not confuse this with pages/Privacy.jsx, which is the DSAR *request* page (a resident
 * exercising a right this notice describes) — a completely different job. This page has no
 * route guard: a notice that only appears after signup is too late to inform the decision
 * to sign up.
 *
 * Every section below is sourced to a specific place in planning/DOMAIN-UK-ESTATE-MANAGEMENT.md
 * so a solicitor reviewing this has a starting point rather than a blank page — see the
 * inline "Source:" comments. Nothing here should be taken as the final wording: fields
 * marked PLACEHOLDER are unresolved product/legal decisions (see
 * planning/LAUNCH-READINESS-TRIAL.md L1-L4), not just unfilled template variables.
 */

function Section({ title, children }) {
    return (
        <section className="mt-8">
            <h2 className="font-heading text-lg font-semibold text-slate-900">{title}</h2>
            <div className="mt-2 text-sm text-slate-700 leading-relaxed space-y-2">{children}</div>
        </section>
    );
}

function Placeholder({ children }) {
    return (
        <span className="bg-amber-100 text-amber-900 px-1.5 py-0.5 rounded font-mono text-[13px]">
            {children}
        </span>
    );
}

export default function PrivacyNotice() {
    return (
        <div className="min-h-screen bg-[#F9FAFB]">
            <div className="max-w-2xl mx-auto px-6 py-12">
                <Link to="/" className="text-sm text-slate-600 hover:text-slate-900 inline-flex items-center gap-1.5">
                    <ArrowLeft size={14} /> Back
                </Link>

                <div className="mt-6 rounded-lg border border-amber-300 bg-amber-50 p-4 flex gap-3" data-testid="privacy-notice-draft-banner">
                    <AlertTriangle size={18} className="text-amber-700 shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-900">
                        <strong>Draft — not yet reviewed by a solicitor.</strong> This describes how the
                        product is built to handle data, sourced to the sections named below. It is a
                        starting point for legal review before real residents' data is collected, not a
                        finished notice. See <code className="text-xs">planning/LAUNCH-READINESS-TRIAL.md</code>.
                    </div>
                </div>

                <h1 className="font-heading text-2xl font-bold text-slate-900 mt-8">Privacy notice</h1>
                <p className="text-sm text-slate-500 mt-1">Draft — last edited 2026-08-24, not yet legally reviewed.</p>

                <Section title="Who controls your data">
                    <p>
                        By default, <strong>your estate's management company</strong> is the data controller
                        for your resident data, once claimed by a director — not this product's operator,
                        who acts as a processor on the management company's instructions. Before an estate
                        is claimed, an individual resident's own workspace holds only their own documents.
                    </p>
                    <p>
                        A management company may instead designate a named managing agency to act as
                        controller on its behalf. If your estate has done this, your specific estate's
                        answer is shown on your{" "}
                        <Link to="/privacy" className="underline">Privacy page</Link> once you have an
                        account — this general notice describes the two possibilities, not a specific
                        answer, because it is shown before you have signed up for or joined a particular
                        estate.
                    </p>
                    <p className="text-xs text-slate-500">
                        Source: DOMAIN-UK-ESTATE-MANAGEMENT.md §7.1. The vendor is never the controller of
                        your resident data under either case. Contact:{" "}
                        <Placeholder>[PLACEHOLDER — vendor contact email]</Placeholder>.
                    </p>
                </Section>

                <Section title="What we collect, and why">
                    <ul className="list-disc pl-5 space-y-1.5">
                        <li><strong>Charge administration</strong> — service charge statements, budgets and
                            per-unit apportionment, so a director and residents can see where money went.</li>
                        <li><strong>Covenant enforcement</strong> — deeds and correspondence, to trace a
                            charge to the clause that permits it.</li>
                        <li><strong>Company membership records</strong> — names, addresses and unit
                            assignments, to run the management company.</li>
                        <li><strong>Community noticeboard</strong> — anything you choose to share with
                            other residents, only if you opt in.</li>
                    </ul>
                    <p className="text-xs text-slate-500">
                        Source: DOMAIN-UK-ESTATE-MANAGEMENT.md §7.2's purpose register. A completed notice
                        should state the UK GDPR Art.6 lawful basis against each purpose — that mapping
                        (contract / legal obligation / legitimate interests / consent) has not been finalised.
                    </p>
                </Section>

                <Section title="How long we keep it">
                    <p>
                        Case records, private documents and case evidence: <strong>6 years</strong>.
                        Estate knowledge base and audit logs: <strong>7 years</strong>.
                    </p>
                    <p className="text-xs text-slate-500">
                        Source: the product's configured default (<code>retention_policy_days</code>), set
                        per estate. DOMAIN-UK-ESTATE-MANAGEMENT.md §7.2 marks these periods{" "}
                        <strong>unverified — to be confirmed with an accountant or solicitor</strong> before
                        they are relied on. Do not treat the numbers above as settled.
                    </p>
                </Section>

                <Section title="Arrears and other sensitive information">
                    <p>
                        Your own arrears position is visible to you and to your estate's directors. It is
                        never shown to other residents by name. See §7.3 of the domain reference — this is
                        a design rule the product enforces, not only a policy statement.
                    </p>
                </Section>

                <Section title="Your rights">
                    <p>
                        You can request a copy of what we hold, ask us to correct it, or ask us to delete
                        or restrict it, from the <Link to="/privacy" className="underline">Privacy</Link>{" "}
                        page once you have an account. Deletion removes your documents and their extracted
                        text; some records are kept in a restricted state rather than deleted outright
                        where the management company has a legal reason to retain them (for example, an
                        ongoing dispute) — you will be told which, and why, at the time.
                    </p>
                </Section>

                <Section title="Security">
                    <p>
                        Each estate's data is kept separate from every other estate's. Your password is
                        never stored in a form we can read back.
                    </p>
                </Section>

                <p className="text-xs text-slate-400 mt-10 pt-6 border-t border-slate-200">
                    See also: <Link to="/terms" className="underline">Terms of service</Link> (also a draft).
                </p>
            </div>
        </div>
    );
}
